import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Building2, 
  Search, 
  RefreshCw, 
  Loader2, 
  AlertCircle,
  Mail,
  Calendar,
  Filter,
  X
} from 'lucide-react';

const AdminCompaniesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const API_URL = getBackendUrl();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('horeca_token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/companies?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err.response?.data?.detail || 'Kon bedrijven niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      const timer = setTimeout(() => {
        fetchCompanies();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, statusFilter, user]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Actief', className: 'bg-emerald-100 text-emerald-700' },
      trial: { label: 'Proefperiode', className: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Opgezegd', className: 'bg-amber-100 text-amber-700' },
      expired: { label: 'Verlopen', className: 'bg-rose-100 text-rose-700' },
      past_due: { label: 'Betaling mislukt', className: 'bg-rose-100 text-rose-700' },
      gift: { label: 'Cadeau', className: 'bg-purple-100 text-purple-700' },
      none: { label: 'Geen', className: 'bg-stone-100 text-stone-700' }
    };
    const config = statusConfig[status] || statusConfig.none;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
          Alle Bedrijven
        </h1>
        <p className="text-[#57534E] mt-1">
          Overzicht van alle geregistreerde bedrijven
        </p>
      </div>

      {/* Filters */}
      <Card className="card-base mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57534E]" />
              <Input
                placeholder="Zoek op naam, email of bedrijfsnaam..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-companies-input"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="active">Actief</SelectItem>
                <SelectItem value="trial">Proefperiode</SelectItem>
                <SelectItem value="gift">Cadeau</SelectItem>
                <SelectItem value="cancelled">Opgezegd</SelectItem>
                <SelectItem value="expired">Verlopen</SelectItem>
                <SelectItem value="none">Geen abonnement</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {(searchQuery || statusFilter !== 'all') && (
              <Button variant="outline" onClick={clearFilters}>
                <X className="w-4 h-4 mr-2" />
                Wissen
              </Button>
            )}

            {/* Refresh */}
            <Button variant="outline" onClick={fetchCompanies} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-500" />
          <p className="text-rose-700">{error}</p>
        </div>
      )}

      {/* Results Count */}
      <div className="mb-4 text-sm text-[#57534E]">
        {companies.length} bedrijven gevonden
      </div>

      {/* Companies Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading && companies.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#C2410C]" />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p className="text-[#57534E]">Geen bedrijven gevonden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bedrijf</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Geregistreerd</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Geldig tot</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-[#C2410C]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1C1917]">{company.name}</p>
                            {company.company_name && (
                              <p className="text-sm text-[#57534E]">{company.company_name}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#57534E]">
                          <Mail className="w-4 h-4" />
                          {company.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#57534E]">
                          <Calendar className="w-4 h-4" />
                          {formatDate(company.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(company.subscription_status)}
                      </TableCell>
                      <TableCell className="text-[#57534E]">
                        {formatDate(company.subscription_end)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCompaniesPage;
